import { type BudgetRowValues, ModifyBudgetRow } from '@/components/ModifyBudgetRow';
import { resolveChannelSubmit } from '@/dashboards/CampaignOptimization/core/channelSubmit';
import { useOptimizationSession } from '@/dashboards/CampaignOptimization/hooks/useOptimizationSession';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { FEATURE_FLAG_SUBJECTS } from '@/features/Casl';

type Props = {
    variable: string;
    onOutOfBoundsChannel?: (info: { channelName: string; min: number; max: number }) => void;
};

export const ChannelRow = ({ variable, onOutOfBoundsChannel }: Props) => {
    const { draft, committed, channelBounds, canEdit, setChannel } = useOptimizationSession();
    const { variableNameMap } = useDashboardGlobalContext();

    const bounds = channelBounds[variable];
    const max = bounds ? Math.max(bounds.min, bounds.max) : 0;
    const recommended = bounds?.recommended ?? 0;
    const formValues: BudgetRowValues = draft?.channels[variable] ?? { locked: false, investment: recommended };

    const handleSubmit = (_key: string, data: BudgetRowValues) => {
        const { value, outOfBounds } = resolveChannelSubmit({ value: data, bounds });
        if (outOfBounds && bounds) {
            onOutOfBoundsChannel?.({ channelName: variableNameMap[variable] || variable, min: bounds.min, max });
        }
        setChannel(variable, value);
    };

    return (
        <ModifyBudgetRow
            disabled={!canEdit}
            title={variableNameMap[variable] ?? variable}
            name={variable}
            sliderConfig={{
                min: bounds?.min ?? 0,
                minRecommended: bounds?.min ?? 0,
                maxRecommended: max,
                recommendedBudget: recommended,
                max,
                investmentStep: committed?.result.investmentStep,
            }}
            formValues={formValues}
            onSubmit={handleSubmit}
            editPermissionFeatureFlag={FEATURE_FLAG_SUBJECTS.CAMPAIGN_OPTIMIZATION_DASHBOARD_MODIFY_BUDGET_EDIT}
        />
    );
};
